import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LiventyLogoMark, LiventyWordmark } from "../components/common/LiventyLogo";

const LAST_UPDATE = "24/06/2026";

function LegalShell({ title, lastUpdate, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Nunito', sans-serif", backgroundColor: "#F8F7FF" }}>
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between bg-white/60 backdrop-blur-sm" style={{ borderBottom: "1px solid #E9E5FF" }}>
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
          <LiventyLogoMark size={32} />
          <LiventyWordmark height={22} variant="dark" />
        </button>
        <button onClick={() => navigate(-1)} className="text-[13px] font-bold inline-flex items-center gap-1.5" style={{ color: "#6B3DF6" }}>
          <ArrowLeft size={14} /> Voltar
        </button>
      </header>

      <main className="flex-1 px-5 sm:px-10 py-10 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-[28px] sm:text-[36px] tracking-tight leading-tight font-bold" style={{ color: "#141023", letterSpacing: "-0.02em" }}>
            {title}
          </h1>
          {lastUpdate && (
            <p className="text-[12.5px] text-zinc-500 mt-2">Última atualização: {lastUpdate}</p>
          )}
          <div className="mt-8 text-[14.5px] text-zinc-700 leading-relaxed space-y-6" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {children}
          </div>

          <div className="mt-12 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <Link to="/" className="text-[13px] text-zinc-500 hover:text-zinc-900">← Voltar à página inicial</Link>
            <div className="flex gap-4 text-[12.5px]">
              <Link to="/privacidade" className="text-zinc-500 hover:text-zinc-900">Privacidade</Link>
              <Link to="/termos" className="text-zinc-500 hover:text-zinc-900">Termos</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const H2 = ({ children }) => <h2 className="text-[20px] font-bold mt-8 mb-3" style={{ color: "#141023" }}>{children}</h2>;
const UL = ({ children }) => <ul className="list-disc list-outside pl-5 space-y-2">{children}</ul>;
const Mail = () => <a href="mailto:contato@liventy.com.br" className="text-violet-700 underline">contato@liventy.com.br</a>;

export function PrivacidadePage() {
  return (
    <LegalShell title="Política de Privacidade" lastUpdate={LAST_UPDATE}>
      <p>
        Esta Política de Privacidade descreve como a <strong>Liventy</strong> ("Liventy", "nós") coleta, utiliza, armazena,
        compartilha e protege dados pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados Pessoais
        (LGPD — Lei nº 13.709/2018)</strong> e demais normas aplicáveis. Ao usar a plataforma, você concorda com as
        práticas aqui descritas.
      </p>

      <H2>1. Quem é o responsável (Controlador e Encarregado)</H2>
      <p>
        <strong>Liventy</strong> — plataforma SaaS de gestão de eventos.<br />
        Contato para assuntos de privacidade e exercício de direitos: <Mail />.<br />
        <strong>Encarregado de Dados (DPO):</strong> Encarregado de Dados — contato pelo e-mail acima.
      </p>

      <H2>2. Papéis no tratamento (importante)</H2>
      <UL>
        <li>
          <strong>Quanto aos seus dados de conta</strong> (cadastro, pagamento, uso), a Liventy atua como
          <strong> Controladora</strong>.
        </li>
        <li>
          <strong>Quanto aos dados de terceiros que você insere</strong> (convidados, fornecedores, contatos), você é o
          <strong> Controlador</strong> desses dados e a Liventy atua como <strong>Operadora</strong> (processadora),
          tratando-os apenas para executar as funções que você solicita. Você é responsável por ter uma base legal válida
          (ex.: consentimento ou legítimo interesse) para inserir e enviar mensagens a esses contatos.
        </li>
      </UL>

      <H2>3. Quais dados coletamos</H2>
      <UL>
        <li><strong>Dados de conta:</strong> nome, e-mail, telefone/WhatsApp, senha (armazenada com hash <strong>bcrypt</strong>, nunca em texto puro) e tipo de perfil (pessoal ou profissional).</li>
        <li><strong>Dados do evento:</strong> informações que você cadastra sobre seu evento (nome, data, local, orçamento, tarefas, cronograma).</li>
        <li><strong>Dados de convidados e contatos:</strong> nome, telefone, e-mail, status de confirmação (RSVP), número de acompanhantes, restrições alimentares/observações, alocação em mesas e registros de check-in (presença, horário).</li>
        <li><strong>Dados de fornecedores e financeiros:</strong> orçamentos, contratos, receitas, despesas, parcelas e fluxo de caixa que você cadastra.</li>
        <li><strong>Imagens e arquivos:</strong> capas de eventos, fotos, inspirações e anexos (ex.: PDFs de contratos) enviados por você, armazenados em provedor de armazenamento de objetos.</li>
        <li><strong>Dados de pagamento:</strong> processados <strong>exclusivamente pela Stripe</strong>. Os dados completos do cartão <strong>nunca</strong> trafegam ou são armazenados em nossos servidores — recebemos apenas identificadores e status da assinatura.</li>
        <li><strong>Dados de uso e segurança:</strong> endereço IP, tipo de navegador/dispositivo, páginas e ações na plataforma, registros (logs) de login e de operações para segurança e auditoria.</li>
      </UL>

      <H2>4. Para que usamos seus dados (finalidades)</H2>
      <UL>
        <li>Criar e manter sua conta e fornecer as funcionalidades da plataforma.</li>
        <li>Processar assinaturas, cobranças e gerenciar planos (Essencial e Plus).</li>
        <li>Enviar comunicações transacionais (confirmação de cadastro, recuperação de senha, avisos do serviço).</li>
        <li>Permitir o envio de convites e mensagens de WhatsApp que você dispara aos seus convidados.</li>
        <li>Gerar recursos de inteligência artificial que você solicitar (ex.: sugestões de texto).</li>
        <li>Garantir segurança, prevenir fraudes e abusos, e cumprir obrigações legais.</li>
      </UL>

      <H2>5. Base legal do tratamento (LGPD, Art. 7º e 11)</H2>
      <UL>
        <li><strong>Execução de contrato</strong> (Art. 7º, V): dados necessários para você usar a plataforma.</li>
        <li><strong>Consentimento</strong> (Art. 7º, I): aceite dos Termos e da Política no cadastro e comunicações de marketing (revogável a qualquer momento).</li>
        <li><strong>Cumprimento de obrigação legal/regulatória</strong> (Art. 7º, II).</li>
        <li><strong>Legítimo interesse</strong> (Art. 7º, IX): segurança, prevenção a fraudes e melhoria do serviço.</li>
      </UL>

      <H2>6. Com quem compartilhamos (operadores e subprocessadores)</H2>
      <p>Compartilhamos dados apenas com prestadores que nos ajudam a operar o serviço, no mínimo necessário:</p>
      <UL>
        <li><strong>Stripe</strong> — processamento de pagamentos e assinaturas.</li>
        <li><strong>Resend</strong> — envio de e-mails transacionais.</li>
        <li><strong>OpenAI</strong> — geração de conteúdo por inteligência artificial (apenas quando você usa esses recursos). O texto enviado é processado para retornar o resultado e, conforme a política da API da OpenAI, <strong>não é utilizado para treinar modelos</strong>.</li>
        <li><strong>Evolution API / WhatsApp</strong> — envio das mensagens que você dispara aos seus convidados.</li>
        <li><strong>Provedor de infraestrutura</strong> — hospedagem, banco de dados e armazenamento de arquivos.</li>
        <li><strong>Autoridades públicas</strong> — somente mediante ordem judicial ou requisição legal válida.</li>
      </UL>
      <p><strong>Nunca vendemos seus dados pessoais.</strong></p>

      <H2>7. Transferência internacional</H2>
      <p>
        Alguns operadores (ex.: Stripe, Resend, OpenAI) podem processar dados em servidores fora do Brasil. Quando isso
        ocorre, adotamos salvaguardas adequadas e exigimos padrões de proteção compatíveis com a LGPD.
      </p>

      <H2>8. Por quanto tempo guardamos</H2>
      <UL>
        <li>Enquanto sua conta estiver ativa.</li>
        <li>Após a exclusão da conta: os dados são removidos; cópias em backup podem ser retidas por até 30 dias.</li>
        <li>Registros de consentimento: até 5 anos, para fins de prova.</li>
        <li>Logs de acesso e segurança: até 6 meses.</li>
        <li>Dados necessários por obrigação legal/fiscal: pelo prazo exigido em lei.</li>
      </UL>

      <H2>9. Seus direitos (LGPD, Art. 18)</H2>
      <p>A qualquer momento você pode:</p>
      <UL>
        <li><strong>Confirmar e acessar</strong> os dados que tratamos sobre você.</li>
        <li><strong>Corrigir</strong> dados incompletos, inexatos ou desatualizados.</li>
        <li><strong>Solicitar exclusão</strong> da conta e dos dados (direito ao esquecimento).</li>
        <li><strong>Portar</strong> seus dados (exportação em formato legível).</li>
        <li><strong>Revogar consentimento</strong> e se opor a tratamentos.</li>
      </UL>
      <p className="mt-3">
        <strong>Como exercer:</strong> exporte seus dados em <strong>Configurações → Privacidade/Exportar dados</strong> (ou
        pelo endpoint autenticado <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-[12px]">GET /api/me/export</code>).
        Para excluir a conta, vá em <strong>Configurações → Excluir conta</strong> ou escreva para <Mail />. Responderemos no
        prazo legal.
      </p>

      <H2>10. Segurança da informação</H2>
      <UL>
        <li>Comunicação criptografada via <strong>HTTPS/TLS</strong> em toda a plataforma e APIs.</li>
        <li>Senhas com hash <strong>bcrypt</strong> — nunca armazenadas em texto puro.</li>
        <li><strong>Isolamento multi-tenant</strong>: cada usuário acessa somente os próprios eventos e dados.</li>
        <li><strong>Limitação de tentativas (rate limit)</strong> e bloqueio progressivo no login contra força bruta.</li>
        <li>Cabeçalhos de segurança HTTP (HSTS, X-Frame-Options, X-Content-Type-Options, CSP) e controle de acesso a arquivos por autorização do dono.</li>
      </UL>

      <H2>11. Cookies e armazenamento local</H2>
      <p>
        Utilizamos armazenamento essencial (ex.: token de sessão guardado no seu navegador) para manter você autenticado e
        para o funcionamento da plataforma. Recursos de análise/marketing, quando existirem, só são ativados com seu
        consentimento.
      </p>

      <H2>12. Menores de idade</H2>
      <p>
        A plataforma é destinada a maiores de <strong>18 anos</strong>. Menores só podem utilizá-la sob supervisão e
        consentimento de pais ou responsáveis legais. Não coletamos intencionalmente dados de menores sem essa autorização.
      </p>

      <H2>13. Alterações nesta Política</H2>
      <p>
        Podemos atualizar esta Política periodicamente. Mudanças relevantes serão comunicadas por e-mail e/ou aviso na
        plataforma, com antecedência razoável.
      </p>

      <H2>14. Contato e reclamações</H2>
      <p>
        Para dúvidas ou solicitações sobre seus dados: <Mail />.<br />
        Você também pode contatar a <strong>ANPD — Autoridade Nacional de Proteção de Dados</strong> (anpd.gov.br).
      </p>
    </LegalShell>
  );
}

export function TermosPage() {
  return (
    <LegalShell title="Termos de Uso" lastUpdate={LAST_UPDATE}>
      <p>
        Estes Termos de Uso ("Termos") regem o acesso e o uso da plataforma <strong>Liventy</strong>. Ao marcar a caixa de
        aceite no cadastro, criar uma conta ou utilizar o serviço, você declara que leu, entendeu e concorda com estes
        Termos e com a <Link to="/privacidade" className="text-violet-700 underline">Política de Privacidade</Link>.
      </p>

      <H2>1. Aceitação</H2>
      <p>
        O uso da Liventy está condicionado à aceitação destes Termos e da Política de Privacidade, manifestada no momento do
        cadastro por meio de caixa de seleção (checkbox). Se você não concorda, não utilize a plataforma.
      </p>

      <H2>2. Sobre o serviço</H2>
      <p>
        A Liventy é uma plataforma SaaS de gestão de eventos (casamentos, festas e eventos em geral) que permite organizar
        convidados, fornecedores, despesas, receitas, cronograma, mesas e assentos, confirmação de presença (RSVP),
        check-in com QR Code, inspirações e o envio de comunicações por WhatsApp, além de recursos de inteligência
        artificial.
      </p>

      <H2>3. Cadastro e conta</H2>
      <UL>
        <li>Você deve ter no mínimo <strong>18 anos</strong> ou utilizar com autorização e supervisão de responsável legal.</li>
        <li>É responsável pela veracidade dos dados informados e por mantê-los atualizados.</li>
        <li>É responsável pela confidencialidade da sua senha e por toda atividade realizada em sua conta.</li>
      </UL>

      <H2>4. Planos, trial e cobrança</H2>
      <UL>
        <li><strong>7 dias de teste gratuito</strong> ao se cadastrar, <strong>sem necessidade de cartão</strong>, com acesso aos recursos do plano Plus durante o período.</li>
        <li><strong>Plano Essencial</strong> (R$ 39,90/mês): 1 evento ativo por vez e os módulos essenciais.</li>
        <li><strong>Plano Plus</strong> (R$ 99,90/mês): até 12 eventos ativos simultâneos e todas as funcionalidades.</li>
        <li>Há opção de cobrança <strong>anual</strong> com condição promocional de lançamento.</li>
        <li>As assinaturas são <strong>processadas pela Stripe</strong> e são recorrentes (mensais ou anuais) até o cancelamento.</li>
        <li>Em caso de <strong>upgrade/downgrade</strong>, ajustamos a mesma assinatura com cálculo proporcional (proration), sem cobrança duplicada.</li>
        <li>Clientes de planos anteriores mantêm suas condições, salvo se optarem por mudar de plano.</li>
      </UL>

      <H2>5. Cancelamento e reembolso</H2>
      <UL>
        <li>Você pode cancelar a qualquer momento em <strong>Configurações → Assinatura</strong>; o acesso permanece até o fim do período já pago.</li>
        <li>Aplica-se o <strong>direito de arrependimento</strong> do Código de Defesa do Consumidor (7 dias para compras online).</li>
        <li>Após esse prazo, valores já cobrados não são reembolsados, mas o cancelamento evita cobranças futuras.</li>
      </UL>

      <H2>6. Suas responsabilidades sobre dados de terceiros</H2>
      <UL>
        <li>Você é responsável pelos dados de convidados/contatos que insere e por ter base legal para tratá-los (LGPD).</li>
        <li>Ao enviar mensagens por WhatsApp, você deve respeitar o consentimento dos destinatários e oferecer meios de recusa (opt-out), além das políticas do WhatsApp.</li>
        <li>A Liventy atua como <strong>operadora</strong> desses dados e os trata apenas para executar as funções solicitadas por você.</li>
      </UL>

      <H2>7. Uso aceitável</H2>
      <p>Você concorda em NÃO:</p>
      <UL>
        <li>Usar a plataforma para atividades ilegais ou fraudulentas;</li>
        <li>Enviar spam, conteúdo malicioso, enganoso ou ofensivo;</li>
        <li>Tentar acessar áreas não autorizadas, burlar limites de plano, fazer engenharia reversa ou explorar vulnerabilidades;</li>
        <li>Violar direitos de terceiros (propriedade intelectual, privacidade, imagem).</li>
      </UL>
      <p>Podemos suspender ou encerrar contas que violarem estes Termos.</p>

      <H2>8. Propriedade intelectual</H2>
      <p>
        O software, design, marca e conteúdos da Liventy são protegidos por direitos autorais e de propriedade industrial.
        Os dados e conteúdos que você insere (convidados, fotos, textos do evento) <strong>permanecem seus</strong>; você nos
        concede licença limitada para processá-los e exibi-los na plataforma, exclusivamente para prestar o serviço.
      </p>

      <H2>9. Limitação de responsabilidade</H2>
      <p>A plataforma é fornecida "no estado em que se encontra". Não nos responsabilizamos por:</p>
      <UL>
        <li>Indisponibilidades temporárias (manutenção ou falhas de provedores);</li>
        <li>Falhas de serviços de terceiros (Stripe, Resend, OpenAI, Evolution API/WhatsApp, hospedagem);</li>
        <li>Perdas decorrentes de força maior (mantemos backups por até 30 dias).</li>
      </UL>
      <p>Na máxima extensão permitida em lei, nossa responsabilidade total não excederá o valor pago por você nos últimos 12 meses.</p>

      <H2>10. Exclusão de conta</H2>
      <p>
        Você pode excluir sua conta a qualquer momento. A exclusão é permanente e remove seus dados (ressalvados backups por
        até 30 dias e obrigações legais). Assinaturas ativas na Stripe são canceladas.
      </p>

      <H2>11. Alterações dos Termos</H2>
      <p>
        Podemos alterar estes Termos a qualquer tempo. Mudanças relevantes serão comunicadas; o uso continuado após a
        vigência representa concordância com a nova versão.
      </p>

      <H2>12. Lei aplicável e foro</H2>
      <p>
        Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da <strong>Comarca de
        Leme/SP</strong> para dirimir controvérsias, com renúncia a qualquer outro, por mais privilegiado que seja.
      </p>

      <H2>13. Contato</H2>
      <p>Dúvidas: <Mail />.</p>
    </LegalShell>
  );
}
